import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Row, Col, Form, Button, Spinner, Alert, Card } from 'react-bootstrap';
import { createOrder } from '../../../../../services/PublicOrderService';

const formatCurrency = (value) =>
    (value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const PAYMENT_OPTIONS = [
    { value: 'PIX', label: 'PIX', icon: 'fa-qrcode', description: 'Pague agora pelo QR Code' },
    { value: 'CREDIT_CARD', label: 'Cartão de Crédito', icon: 'fa-credit-card', description: 'Pague agora pelo link' },
];

const getRemainingSeconds = (expiresAt) => {
    if (!expiresAt) return null;
    return Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000));
};

const formatRemaining = (seconds) => {
    if (seconds === null) return '';
    const minutes = Math.floor(seconds / 60).toString().padStart(2, '0');
    const restSeconds = (seconds % 60).toString().padStart(2, '0');
    return `${minutes}:${restSeconds}`;
};

const StepConfirmation = ({
    companyUuid,
    branch,
    cart,
    customerData,
    obs,
    onObsChange,
    orderResult,
    onConfirm,
    onBack,
    onNewOrder,
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [confirmed, setConfirmed] = useState(!!orderResult);
    const [result, setResult] = useState(orderResult);
    const [paymentMethod, setPaymentMethod] = useState('');
    const [pixRemainingSeconds, setPixRemainingSeconds] = useState(getRemainingSeconds(orderResult?.pixExpiresAt));

    const cartItems = Object.values(cart).filter((i) => i.qty > 0);
    const cartTotal = cartItems.reduce((s, i) => s + (i.product.price || 0) * i.qty, 0);

    const getCustomerFullName = () =>
        [customerData?.firstName, customerData?.lastName].filter(Boolean).join(' ');

    useEffect(() => {
        if (!result?.pixExpiresAt) {
            setPixRemainingSeconds(null);
            return undefined;
        }

        const updateRemaining = () => setPixRemainingSeconds(getRemainingSeconds(result.pixExpiresAt));
        updateRemaining();
        const timer = window.setInterval(updateRemaining, 1000);
        return () => window.clearInterval(timer);
    }, [result?.pixExpiresAt]);

    const copyPixCode = async () => {
        if (!result?.pixCopyPaste) return;

        try {
            await navigator.clipboard.writeText(result.pixCopyPaste);
            setError('');
        } catch (err) {
            setError('Não foi possível copiar o código Pix automaticamente.');
        }
    };

    const handleConfirm = async () => {
        if (!paymentMethod) {
            setError('Selecione uma forma de pagamento para continuar.');
            return;
        }
        try {
            setLoading(true);
            setError('');

            const orderData = {
                branchId: branch.id,
                customerName: getCustomerFullName(),
                customerPhone: customerData.phone.replace(/\D/g, ''),
                customerDocument: customerData.document?.replace(/\D/g, '') || null,
                obs: obs || null,
                paymentMethod,
                items: cartItems.map((i) => ({
                    productId: i.product.id,
                    qty: i.qty,
                    price: i.product.price,
                })),
            };

            const response = await createOrder(companyUuid, orderData);
            setResult(response.data);
            setConfirmed(true);
            onConfirm(response.data);
        } catch (err) {
            if (err.response?.data?.message) {
                setError(err.response.data.message);
            } else {
                setError('Não foi possível criar o pedido. Tente novamente.');
            }
        } finally {
            setLoading(false);
        }
    };

    // ── Tela de sucesso ──────────────────────────────────────────────────
    if (confirmed && result) {
        const hasOnlinePayment = result.paymentUrl || result.pixCopyPaste;
        const pixExpired = result.pixCopyPaste && pixRemainingSeconds === 0;

        return (
            <div className="step-success text-center py-4">
                <div className="success-icon mb-4">
                    <div className="success-circle">
                        <i className="fa fa-check fa-3x text-white"></i>
                    </div>
                </div>

                <h3 className="text-success mb-3">
                    {hasOnlinePayment ? 'Pedido gerado. Faça o pagamento.' : 'Pedido realizado com sucesso!'}
                </h3>
                <p className="text-muted mb-4">
                    {hasOnlinePayment
                        ? 'Use os dados de pagamento abaixo para concluir seu pedido.'
                        : 'Seu pedido foi recebido. Guarde o código abaixo para acompanhamento.'}
                </p>

                {error && (
                    <Alert variant="warning" className="text-start">
                        <i className="fa fa-exclamation-circle me-2"></i>
                        {error}
                    </Alert>
                )}

                {/* Código do pedido */}
                <Card className="order-code-card mb-4">
                    <Card.Body className="py-4">
                        <small className="text-white d-block mb-2">Código do pedido</small>
                        <h2 className="order-uuid mb-0 font-monospace">
                            {result.uuid?.substring(0, 8).toUpperCase()}
                        </h2>
                    </Card.Body>
                </Card>

                {/* Resumo */}
                <Card className="summary-card text-start mb-4">
                    <Card.Body>
                        <h5 className="mb-3">
                            <i className="fa fa-receipt me-2 text-primary"></i>
                            Itens do pedido
                        </h5>
                        {cartItems.map((item) => (
                            <div
                                key={item.product.id}
                                className="d-flex justify-content-between align-items-center py-2 border-bottom"
                            >
                                <span>
                                    {item.qty}× {item.product.name}
                                </span>
                                <span className="fw-semibold">
                                    {formatCurrency((item.product.price || 0) * item.qty)}
                                </span>
                            </div>
                        ))}
                        <div className="d-flex justify-content-between align-items-center pt-3">
                            <span className="fw-bold fs-5">Total</span>
                            <span className="fw-bold fs-5 text-primary">{formatCurrency(cartTotal)}</span>
                        </div>
                        {(result.paymentMethod || paymentMethod) && (
                            <div className="d-flex align-items-center gap-2 mt-3 pt-3 border-top text-muted small">
                                <i className="fa fa-credit-card"></i>
                                <span>{PAYMENT_OPTIONS.find(p => p.value === (result.paymentMethod || paymentMethod))?.label || result.paymentMethod || paymentMethod}</span>
                            </div>
                        )}
                    </Card.Body>
                </Card>

                {(result.paymentUrl || result.pixCopyPaste) && (
                    <Card className="public-payment-card text-start mb-4">
                        <Card.Body>
                            <h5 className="mb-3">
                                <i className="fa fa-lock me-2 text-primary"></i>
                                Pagamento do pedido
                            </h5>

                            {result.paymentUrl && (
                                <>
                                    <p className="text-muted mb-3">
                                        Clique no botão abaixo para abrir o link seguro de pagamento.
                                    </p>
                                    <Button
                                        variant="success"
                                        size="lg"
                                        className="w-100"
                                        onClick={() => window.open(result.paymentUrl, '_blank', 'noopener,noreferrer')}
                                    >
                                        <i className="fa fa-external-link-alt me-2"></i>
                                        Pagar agora
                                    </Button>
                                </>
                            )}

                            {result.pixCopyPaste && (
                                <>
                                    {pixExpired ? (
                                        <Alert variant="warning" className="mb-0">
                                            <i className="fa fa-clock me-2"></i>
                                            Este Pix expirou. Consulte o pedido ou faça um novo pedido para gerar outro pagamento.
                                        </Alert>
                                    ) : (
                                        <>
                                            {pixRemainingSeconds !== null && (
                                                <div className="pix-expiration-box mb-3">
                                                    <span>Tempo para pagamento</span>
                                                    <strong>{formatRemaining(pixRemainingSeconds)}</strong>
                                                </div>
                                            )}
                                            {result.pixQrCode && (
                                                <div className="public-pix-qrcode mb-3">
                                                    <img src={result.pixQrCode} alt="QR Code Pix" />
                                                </div>
                                            )}
                                            <p className="text-muted small mb-2">Pix copia e cola</p>
                                            <code className="public-pix-code d-block mb-3">{result.pixCopyPaste}</code>
                                            <Button variant="success" className="w-100" onClick={copyPixCode}>
                                                <i className="fa fa-copy me-2"></i>
                                                Copiar código Pix
                                            </Button>
                                        </>
                                    )}
                                </>
                            )}
                        </Card.Body>
                    </Card>
                )}

                <Alert variant="info" className="text-start">
                    <i className="fa fa-info-circle me-2"></i>
                    Anote o código acima para acompanhar seu pedido.
                </Alert>

                <div className="d-flex justify-content-center gap-2 flex-wrap mt-3">
                    <Button as={Link} to={`/order/${companyUuid}/track`} variant="outline-primary" size="lg">
                        <i className="fa fa-search me-2"></i>
                        Acompanhar pedido
                    </Button>
                    <Button variant="primary" size="lg" onClick={onNewOrder}>
                        <i className="fa fa-plus me-2"></i>
                        Fazer novo pedido
                    </Button>
                </div>
            </div>
        );
    }

    // ── Revisão antes de confirmar ────────────────────────────────────────
    return (
        <div className="step-confirmation">
            <div className="step-header mb-3">
                <h4 className="step-title">
                    <i className="fa fa-check-circle me-2 text-primary"></i>
                    Confirmar pedido
                </h4>
                <p className="step-subtitle text-muted">
                    Revise os dados antes de finalizar.
                </p>
            </div>

            {error && (
                <Alert variant="danger" className="mb-3">
                    <i className="fa fa-exclamation-circle me-2"></i>
                    {error}
                </Alert>
            )}

            <Row className="g-3">
                {/* Filial */}
                <Col md={6}>
                    <Card className="summary-card compact-summary-card h-100">
                        <Card.Body>
                            <h6 className="fw-bold mb-2">
                                <i className="fa fa-store me-2 text-primary"></i>
                                Filial
                            </h6>
                            <p className="mb-0">{branch?.name}</p>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Cliente */}
                <Col md={6}>
                    <Card className="summary-card compact-summary-card h-100">
                        <Card.Body>
                            <h6 className="fw-bold mb-2">
                                <i className="fa fa-user me-2 text-primary"></i>
                                Cliente
                            </h6>
                            <p className="mb-1">{getCustomerFullName()}</p>
                            <p className="mb-0 text-muted small">{customerData?.phone}</p>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Itens */}
                <Col md={12}>
                    <Card className="summary-card compact-summary-card">
                        <Card.Body>
                            <h6 className="fw-bold mb-3">
                                <i className="fa fa-shopping-basket me-2 text-primary"></i>
                                Itens ({cartItems.length})
                            </h6>
                            {cartItems.map((item) => (
                                <div
                                    key={item.product.id}
                                    className="d-flex justify-content-between align-items-center py-2 border-bottom"
                                >
                                    <span>
                                        {item.qty}× {item.product.name}
                                    </span>
                                    <span className="fw-semibold">
                                        {formatCurrency((item.product.price || 0) * item.qty)}
                                    </span>
                                </div>
                            ))}
                            <div className="d-flex justify-content-between align-items-center pt-3">
                                <span className="fw-bold">Total</span>
                                <span className="fw-bold text-primary fs-5">{formatCurrency(cartTotal)}</span>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Forma de pagamento */}
                <Col md={12}>
                    <div className="fw-semibold mb-2">
                        Forma de pagamento <span className="text-danger">*</span>
                    </div>
                    <Row className="g-2 payment-options-compact">
                        {PAYMENT_OPTIONS.map((opt) => (
                            <Col xs={6} key={opt.value}>
                                <div
                                    className={`payment-option-card${paymentMethod === opt.value ? ' selected' : ''}`}
                                    onClick={() => { setPaymentMethod(opt.value); setError(''); }}
                                    role="button"
                                >
                                    <i className={`fa ${opt.icon} fa-lg mb-1`}></i>
                                    <div className="payment-option-label">{opt.label}</div>
                                    <div className="payment-option-desc">{opt.description}</div>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </Col>

                {/* Observações */}
                <Col md={12}>
                    <Form.Group>
                        <Form.Label className="fw-semibold">
                            Observações <span className="text-muted fw-normal">(opcional)</span>
                        </Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={4}
                            placeholder="Alguma observação para o pedido?"
                            value={obs}
                            onChange={(e) => onObsChange(e.target.value)}
                        />
                    </Form.Group>
                </Col>
            </Row>

            <div className="d-flex justify-content-between mt-3">
                <Button variant="outline-secondary" onClick={onBack} disabled={loading}>
                    <i className="fa fa-arrow-left me-2"></i>Voltar
                </Button>
                <Button variant="primary" onClick={handleConfirm} disabled={loading}>
                    {loading ? (
                        <><Spinner size="sm" className="me-2" />Processando...</>
                    ) : (
                        <><i className="fa fa-check me-2"></i>Confirmar Pedido</>
                    )}
                </Button>
            </div>
        </div>
    );
};

export default StepConfirmation;
